import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RiskBadge } from "../components/RiskBadge";
import { STATUS_LABEL } from "../lib/constants";
import { ChevronLeft } from "lucide-react";

import { CaseTimeline } from "../components/case/CaseTimeline";
import { CaseDossier } from "../components/case/CaseDossier";
import { CaseClinicsList } from "../components/case/CaseClinicsList";
import { CaseProfessionalsList } from "../components/case/CaseProfessionalsList";
import { BookProfessionalModal } from "../components/case/BookProfessionalModal";
import { CaseClosePanel } from "../components/case/CaseClosePanel";
import { CaseClosedSummary } from "../components/case/CaseClosedSummary";
import { CaseChat } from "../components/case/CaseChat";
import { TemplatesModal } from "../components/case/TemplatesModal";
import { ClinicVitrineModal } from "../components/case/ClinicVitrineModal";
import { SharedClinicsPanel, AppointmentsPanel } from "../components/case/SharedClinicsPanel";
import { PostReservationActions } from "../components/case/PostReservationActions";
import { ArrivalFlow } from "../components/case/ArrivalFlow";
import AdmissionScheduleCard from "../components/case/AdmissionScheduleCard";

const OPEN_STATUSES = new Set(["analise", "consultor_designado", "clinica_selecionada", "reserva_confirmada"]);

const ConsultantCase = () => {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [messages, setMessages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [draft, setDraft] = useState("");
  const [closeForm, setCloseForm] = useState({
    outcome: "internacao", clinic_id: "", professional_id: "", admission_datetime: "",
    modality: "voluntária", responsible: "",
    reason: "desistencia", reason_details: "",
  });
  const [templates, setTemplates] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [selectedTpl, setSelectedTpl] = useState("default");
  const [closeReasons, setCloseReasons] = useState([]);
  const [vitrineClinicId, setVitrineClinicId] = useState(null);
  const [bookProfModal, setBookProfModal] = useState(null); // profissional a reservar
  const [clinicDraft, setClinicDraft] = useState("");

  const load = useCallback(async () => {
    const [cc, mm, dd, tt, rr] = await Promise.all([
      api.get(`/cases/${caseId}`),
      api.get(`/cases/${caseId}/messages`),
      api.get(`/cases/${caseId}/documents`),
      api.get(`/consultant/templates`),
      api.get(`/close-reasons`),
    ]);
    setC(cc.data);
    setMessages(mm.data);
    setDocs(dd.data);
    setTemplates(tt.data.templates || []);
    setCloseReasons(rr.data.reasons || []);
    // Ramificação por intenção: consulta → profissionais; demais → clínicas.
    const isConsulta = cc.data.family_intent === "consulta";
    if (isConsulta) {
      const [pr, cl] = await Promise.all([
        api.get(`/professionals/match/${caseId}`).catch(() => ({ data: [] })),
        api.get(`/clinics/match/${caseId}`).catch(() => ({ data: [] })),
      ]);
      setProfessionals(pr.data || []);
      setClinics(cl.data || []);
      // Default do painel de encerramento = consulta_realizada
      setCloseForm((f) => ({ ...f, outcome: "consulta_realizada", modality: "online" }));
    } else {
      const cl = await api.get(`/clinics/match/${caseId}`);
      setClinics(cl.data);
    }
    if (cc.data.clinic_id) setCloseForm((f) => ({ ...f, clinic_id: cc.data.clinic_id }));
  }, [caseId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const i = setInterval(
      () => api.get(`/cases/${caseId}/messages`).then(({ data }) => setMessages(data)).catch(() => {}),
      5000,
    );
    return () => clearInterval(i);
  }, [caseId]);

  if (!c) return <div className="p-10 text-slate-500">Carregando…</div>;

  const assign = async () => {
    await api.post(`/consultant/cases/${caseId}/assign`);
    toast.success("Caso atribuído a você");
    load();
  };
  const reserve = async (clinic_id) => {
    await api.post(`/cases/${caseId}/reserve`, { clinic_id, notes: "Reserva realizada pelo consultor" });
    toast.success("Reserva confirmada");
    load();
  };
  const closeCase = async () => {
    if (closeForm.outcome === "internacao") {
      if (!closeForm.clinic_id || !closeForm.admission_datetime || !closeForm.responsible) {
        toast.error("Preencha clínica, data/hora e responsável"); return;
      }
    } else if (closeForm.outcome === "consulta_realizada") {
      if (!closeForm.professional_id || !closeForm.admission_datetime) {
        toast.error("Selecione o profissional e a data/hora da consulta"); return;
      }
    } else if (!closeForm.reason) {
      toast.error("Informe o motivo da não internação"); return;
    }
    await api.post(`/cases/${caseId}/close`, closeForm);
    toast.success("Caso concluído com sucesso");
    load();
  };
  const indicateProfessional = async (prof) => {
    const name = prof.display_name || prof.name || "profissional";
    const link = `${window.location.origin}/profissionais/${prof.id}`;
    const council = [prof.council_type, prof.council_number, prof.council_state].filter(Boolean).join(" ");
    // Attachment estruturado — família vê como card, não texto solto
    const attachment = {
      type: "professional_card",
      professional_id: prof.id,
      snapshot: {
        name, council,
        photo_url: prof.photo_url || null,
        categories: prof.categories || [],
        city: prof.city || null,
        session_value: prof.session_value ?? null,
        bio: (prof.bio || "").slice(0, 160),
        profile_url: link,
      },
    };
    const content =
      `Olá! Sugerimos uma consulta *online* com **${name}**` +
      (council ? ` (${council})` : "") +
      `. Confira o perfil completo e nos diga se quer seguir com este(a) profissional ou prefere outra sugestão.`;
    try {
      const { data } = await api.post(`/cases/${caseId}/messages`, { content, attachment });
      setMessages((prev) => [...prev, data]);
      toast.success(`${name} indicado(a) à família`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao indicar profissional");
    }
  };
  const openWhatsapp = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/whatsapp?template_id=${selectedTpl}`);
      window.open(data.url, "_blank", "noopener");
    } catch {
      toast.error("Não foi possível gerar o link WhatsApp");
    }
  };
  const signDossier = async () => {
    if (!window.confirm("Assinar digitalmente este dossiê? A assinatura ficará registrada com seu nome, data e hash SHA-256.")) return;
    try {
      await api.post(`/cases/${caseId}/sign`);
      toast.success("Dossiê assinado com sucesso");
      load();
    } catch { toast.error("Erro ao assinar"); }
  };
  const saveTemplates = async () => {
    try {
      const { data } = await api.put(`/consultant/templates`, { templates });
      setTemplates(data.templates);
      toast.success("Templates salvos");
      setShowTpl(false);
    } catch { toast.error("Erro ao salvar templates"); }
  };
  const sendMsg = async () => {
    if (!draft.trim()) return;
    const { data } = await api.post(`/cases/${caseId}/messages`, { content: draft.trim() });
    setMessages([...messages, data]);
    setDraft("");
  };
  const sendClinicMsg = async () => {
    if (!clinicDraft.trim()) return;
    try {
      const { data } = await api.post(`/cases/${caseId}/messages/clinic`, { content: clinicDraft.trim() });
      setMessages([...messages, data]);
      setClinicDraft("");
      toast.success("Mensagem enviada à Unidade Parceira");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    }
  };

  // Código anônimo da clínica reservada (para exibir na aba Clínica do chat)
  const reservedClinic = clinics.find((cl) => cl.id === c.clinic_id);
  const reservedClinicCode = reservedClinic?.code || null;

  const scrollTo = (testId) => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <Link to="/consultor" className="text-sm text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Voltar ao painel
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs font-mono text-slate-500 uppercase tracking-widest">{c.protocol}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">{c.patient_name}, {c.patient_age}</h1>
          <div className="text-sm text-slate-600 mt-1">{c.city} · {c.substance} · Solicitante: {c.family_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <RiskBadge level={c.risk_level} size="lg" />
          <span className="text-xs px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-full text-slate-600">{STATUS_LABEL[c.status]}</span>
        </div>
      </div>

      <CaseTimeline timeline={c.timeline} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <CaseDossier caseData={c} docs={docs} caseId={caseId} onAssign={assign} onSign={signDossier} />

          {c.status !== "concluido" && (
            c.family_intent === "consulta" ? (
              <CaseProfessionalsList
                professionals={professionals}
                onIndicate={indicateProfessional}
                onBook={(p) => setBookProfModal(p)}
                onViewDetails={(id) => window.open(`/profissionais/${id}`, "_blank", "noopener")}
              />
            ) : (
              <CaseClinicsList
                caseData={c} clinics={clinics}
                templates={templates}
                selectedTpl={selectedTpl} setSelectedTpl={setSelectedTpl}
                onOpenTemplates={() => setShowTpl(true)}
                onWhatsapp={openWhatsapp}
                onReserve={reserve}
                onViewDetails={(id) => setVitrineClinicId(id)}
              />
            )
          )}

          <SharedClinicsPanel shared={c.shared_clinics} />
          <AppointmentsPanel appointments={c.appointments} />

          <PostReservationActions
            caseData={c}
            clinicCode={reservedClinicCode}
            onGoFamilyChat={() => scrollTo("case-chat")}
            onGoClinicChat={() => { scrollTo("case-chat"); setTimeout(() => document.querySelector('[data-testid="chat-tab-clinica"]')?.click(), 300); }}
            onGoClose={() => scrollTo("close-case-panel")}
          />

          {/* Agendar internação (após clínica confirmar reserva) — Iter 68.12 */}
          <AdmissionScheduleCard caseObj={c} role="consultor" onDone={load} />

          {/* Fluxo chegada — visível quando reserva confirmada OU quando já foi confirmada. */}
          {(c.status === "reserva_confirmada" || c.arrival) && (
            <ArrivalFlow caseObj={c} role="consultor" onDone={load} />
          )}

          {OPEN_STATUSES.has(c.status) && (
            <div data-testid="close-case-panel">
              <CaseClosePanel
                caseData={c} closeForm={closeForm} setCloseForm={setCloseForm}
                closeReasons={closeReasons} clinics={clinics} professionals={professionals}
                onCloseCase={closeCase}
              />
            </div>
          )}

          {c.status === "concluido" && c.closure && (
            <CaseClosedSummary caseData={c} caseId={caseId} closeReasons={closeReasons} onReloaded={load} />
          )}
        </div>

        <div>
          <CaseChat
            messages={messages}
            draft={draft} setDraft={setDraft} onSend={sendMsg}
            clinicDraft={clinicDraft} setClinicDraft={setClinicDraft} onSendToClinic={sendClinicMsg}
            hasClinicTab={true}
            clinicCode={reservedClinicCode}
            clinicReserved={["reserva_confirmada", "concluido"].includes(c.status)}
            familySelection={c.family_selection}
            caseId={caseId}
          />
        </div>
      </div>

      {showTpl && (
        <TemplatesModal
          templates={templates} setTemplates={setTemplates}
          onSave={saveTemplates} onClose={() => setShowTpl(false)}
        />
      )}

      {vitrineClinicId && (
        <ClinicVitrineModal
          clinicId={vitrineClinicId}
          caseData={c}
          onClose={() => setVitrineClinicId(null)}
          onShared={load}
          onScheduled={load}
          onReserve={(id) => { setVitrineClinicId(null); reserve(id); }}
        />
      )}

      {bookProfModal && (
        <BookProfessionalModal
          caseData={c}
          professional={bookProfModal}
          onClose={() => setBookProfModal(null)}
          onBooked={() => { load(); }}
        />
      )}
    </div>
  );
};

export default ConsultantCase;
